import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Preferences } from "@capacitor/preferences";
import { AutoTaskAuthContext } from "./AutoTaskContext";
import unofficialModeIndicatorGIF from "../assets/icons/unofficial-mode.gif";

/**
 * Global source of truth for the pawn "official / unofficial" mode.
 *
 *  - mode 1 = Official
 *  - mode 2 = Unofficial
 *
 * Mode is persisted per store under `pawn_mode_status_<storeId>` (the same key
 * the individual pawn screens already used) so it survives reloads and stays
 * consistent with anything reading the raw preference.
 *
 * Switching is done entirely through a hidden keyboard gesture (see below) —
 * there is intentionally no visible button to toggle it.
 *
 *   Official  →  Unofficial :  F12, then type "rs" / "RS", then Enter
 *   Unofficial →  Official   :  F12
 *
 * While in unofficial mode a small, text-free indicator sits in the very
 * bottom-right corner of the screen (mirrors the old desktop app) so only the
 * operator who knows about it would ever notice.
 */
export const PawnModeContext = createContext();

export const usePawnMode = () => useContext(PawnModeContext);

const OFFICIAL = 1;
const UNOFFICIAL = 2;
const SECRET = "rs"; // what must be typed between F12 and Enter to go unofficial
const ARM_TIMEOUT_MS = 4000; // auto-cancel the gesture if not confirmed in time

export const PawnModeProvider = ({ children }) => {
  const { currentStoreInfo } = useContext(AutoTaskAuthContext) || {};
  const storeId = currentStoreInfo?.id;

  const [mode, setModeState] = useState(OFFICIAL);
  const [modeLoaded, setModeLoaded] = useState(false);
  const loadedForStore = useRef(null);

  /* ── Load the persisted mode whenever the active store changes ─────────── */
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (storeId === undefined || storeId === null) {
        // No store yet — don't keep consumers waiting; default to official.
        setModeLoaded(true);
        return;
      }
      if (loadedForStore.current === storeId) return;
      try {
        const { value } = await Preferences.get({
          key: `pawn_mode_status_${storeId}`,
        });
        if (!cancelled) {
          const saved = value ? parseInt(value, 10) : OFFICIAL;
          setModeState(saved === UNOFFICIAL ? UNOFFICIAL : OFFICIAL);
        }
      } catch {
        if (!cancelled) setModeState(OFFICIAL);
      } finally {
        if (!cancelled) {
          loadedForStore.current = storeId;
          setModeLoaded(true);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  /* ── Set + persist mode ────────────────────────────────────────────────── */
  const setMode = useCallback(
    (next) => {
      const value = next === UNOFFICIAL ? UNOFFICIAL : OFFICIAL;
      setModeState(value);
      if (storeId !== undefined && storeId !== null) {
        Preferences.set({
          key: `pawn_mode_status_${storeId}`,
          value: String(value),
        }).catch(() => {});
      }
    },
    [storeId],
  );

  const toggleMode = useCallback(
    () => setMode(mode === UNOFFICIAL ? OFFICIAL : UNOFFICIAL),
    [mode, setMode],
  );

  /* ── Hidden keyboard gesture ───────────────────────────────────────────── */
  const armedRef = useRef(false); // are we capturing the secret after F12?
  const bufferRef = useRef(""); // characters typed while armed
  const disarmTimerRef = useRef(null);

  useEffect(() => {
    const disarm = () => {
      armedRef.current = false;
      bufferRef.current = "";
      if (disarmTimerRef.current) {
        clearTimeout(disarmTimerRef.current);
        disarmTimerRef.current = null;
      }
    };

    const onKeyDown = (e) => {
      if (e.key === "F12") {
        // Always swallow F12 so it neither opens dev tools nor leaks the gesture.
        e.preventDefault();
        if (mode === UNOFFICIAL) {
          // A single F12 drops straight back to official mode.
          disarm();
          setMode(OFFICIAL);
        } else if (armedRef.current) {
          // F12 again while waiting for the secret cancels the attempt.
          disarm();
        } else {
          // Arm: start listening for the secret; auto-cancel shortly after.
          armedRef.current = true;
          bufferRef.current = "";
          disarmTimerRef.current = setTimeout(disarm, ARM_TIMEOUT_MS);
        }
        return;
      }

      if (!armedRef.current) return;

      // While armed we consume every keystroke so the secret is never typed
      // into a focused field and never visible on screen.
      if (e.key === "Enter") {
        e.preventDefault();
        const ok = bufferRef.current.trim().toLowerCase() === SECRET;
        disarm();
        if (ok) setMode(UNOFFICIAL);
      } else if (e.key === "Escape") {
        e.preventDefault();
        disarm();
      } else if (e.key === "Backspace") {
        e.preventDefault();
        bufferRef.current = bufferRef.current.slice(0, -1);
      } else if (e.key.length === 1) {
        e.preventDefault();
        bufferRef.current += e.key;
      }
    };

    // Capture phase so we win even when an input is focused.
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      disarm();
    };
  }, [mode, setMode]);

  return (
    <PawnModeContext.Provider
      value={{
        mode,
        isUnofficial: mode === UNOFFICIAL,
        setMode,
        toggleMode,
        modeLoaded,
      }}
    >
      {children}
      {mode === UNOFFICIAL && <UnofficialModeIndicator />}
    </PawnModeContext.Provider>
  );
};

/**
 * Text-free unofficial-mode indicator, tucked into the extreme bottom-right
 * corner of the viewport. Small, semi-transparent and non-interactive so it
 * reads as an incidental status dot to anyone who isn't looking for it.
 */
const UnofficialModeIndicator = () => (
  <img
    src={unofficialModeIndicatorGIF}
    alt=""
    aria-hidden="true"
    draggable={false}
    style={{
      position: "fixed",
      right: 6,
      bottom: 6,
      width: 14,
      height: 14,
      borderRadius: "50%",
      opacity: 0.9,
      pointerEvents: "none",
      userSelect: "none",
      zIndex: 2147483647,
    }}
  />
);
